import { memo } from 'react'

import { ASPECT, VIEW_W, type Poly } from '@/geo/types'

/**
 * An occluder layer (§4.3.1 `Layer`): the outline of the cut-out plus the
 * **baseline** — the row where it touches the ground. The z-order rule in
 * §8.1 is exactly "feet above the baseline ⇒ draw behind", so this one line
 * is the whole walk-behind feature.
 */

export interface LayerShapeProps {
  id: string
  z: 'back' | 'mid' | 'front'
  baselineY?: number
  /** Outline of the region this layer is cut from, when it has one. */
  poly?: Poly
  colour: string
  selected?: boolean
  hovered?: boolean
  label?: string
  onPointerDown?: (ev: React.PointerEvent<SVGElement>) => void
}

export const LayerShape = memo(function LayerShape({ id, z, baselineY, poly, colour, selected, hovered, label, onPointerDown }: LayerShapeProps) {
  const width = selected ? 0.45 : hovered ? 0.36 : 0.26
  const y = baselineY === undefined ? null : baselineY * ASPECT

  return (
    <g role="img" aria-label={`${label ?? id}, ${z}${baselineY === undefined ? ', always in front' : ''}`}>
      <title>{baselineY === undefined ? 'Always in front of characters' : 'Characters standing above this line pass behind'}</title>
      {poly ? (
        <polygon
          data-id={id}
          points={poly.outer.map((p) => `${p[0]},${p[1] * ASPECT}`).join(' ')}
          fill="none"
          stroke={colour}
          strokeWidth={width}
          strokeDasharray="1.6 1"
          vectorEffect="non-scaling-stroke"
          style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
          onPointerDown={onPointerDown}
        />
      ) : null}
      {y !== null ? (
        <>
          <line
            x1={0}
            y1={y}
            x2={VIEW_W}
            y2={y}
            stroke={colour}
            strokeWidth={width}
            strokeDasharray="0.9 0.9"
            vectorEffect="non-scaling-stroke"
            pointerEvents="none"
          />
          <rect
            data-id={id}
            data-baseline={id}
            x={0}
            y={y - 0.8}
            width={VIEW_W}
            height={1.6}
            fill="transparent"
            style={{ cursor: 'ns-resize' }}
            onPointerDown={onPointerDown}
          />
        </>
      ) : null}
    </g>
  )
})
